/**
 * Registry of the Prescriptions-tab report columns, mirroring the Coda page "Reports: columns"
 * (82 fields, 17 on by default). Field order here is the canonical order used by the Columns dialog
 * and by the table: consecutive fields sharing a `cellGroup` are rendered as one stacked cell.
 */
import {} from './fields.js';
export const REPORT_GROUP_ORDER = [
    'prescription',
    'validation',
    'patient',
    'release',
    'clinical',
    'production',
    'administration',
    'cost',
];
function f(id, group, type, opts = {}) {
    return { id, labelKey: id, group, tab: 'prescriptions', type, ...opts, default: opts.default ?? false };
}
export const PRESCRIPTION_REPORT_FIELDS = [
    // Prescription
    f('prescriptionNumber', 'prescription', 'name', { default: true, sortKey: 'internalId' }),
    f('applicationDate', 'prescription', 'date', { default: true, sortKey: 'therapyDate' }),
    f('applicationTime', 'prescription', 'name', { sortKey: 'time' }),
    f('status', 'prescription', 'name', { default: true, sortKey: 'status' }),
    f('prescriptionType', 'prescription', 'name'),
    f('origin', 'prescription', 'name'),
    f('protocol', 'prescription', 'name', { default: true, cellGroup: 'protocol' }),
    f('cycle', 'prescription', 'number', { default: true, cellGroup: 'protocol', sortKey: 'cycle' }),
    f('day', 'prescription', 'number', { default: true, cellGroup: 'protocol', sortKey: 'day' }),
    f('prescribedBy', 'prescription', 'name'),
    f('prescribedOn', 'prescription', 'date', { sortKey: 'createdAt' }),
    f('autIdem', 'prescription', 'name'),
    f('prescriptionNote', 'prescription', 'longText'),
    f('pharmacyNote', 'prescription', 'longText'),
    // Patient
    f('patientName', 'patient', 'name', { default: true, cellGroup: 'patient' }),
    f('hospitalId', 'patient', 'name', { default: true, cellGroup: 'patient' }),
    f('nationalNumber', 'patient', 'name'),
    f('insuranceNumber', 'patient', 'name'),
    f('insuranceCompany', 'patient', 'name'),
    f('dateOfBirth', 'patient', 'date'),
    f('age', 'patient', 'number'),
    f('sex', 'patient', 'name'),
    f('section', 'patient', 'name'),
    f('ward', 'patient', 'name', { default: true }),
    f('customer', 'patient', 'name'),
    f('diagnosis', 'patient', 'name'),
    f('privatePatient', 'patient', 'name'),
    // Clinical
    f('responsibleDoctor', 'clinical', 'name', { default: true }),
    f('activeSubstance', 'clinical', 'name', { default: true, cellGroup: 'substance' }),
    f('prescribedDose', 'clinical', 'number', { default: true, cellGroup: 'substance', decimals: true }),
    f('doseUnit', 'clinical', 'name', { default: true, cellGroup: 'substance' }),
    f('doseBasis', 'clinical', 'name'),
    f('standardDose', 'clinical', 'number', { decimals: true }),
    f('doseChangePercent', 'clinical', 'number', { decimals: true }),
    f('height', 'clinical', 'number', { decimals: true }),
    f('weight', 'clinical', 'number', { decimals: true }),
    f('weightType', 'clinical', 'name'),
    f('bsa', 'clinical', 'number', { decimals: true }),
    f('bsaFormula', 'clinical', 'name'),
    f('gfr', 'clinical', 'number', { decimals: true }),
    f('gfrMethod', 'clinical', 'name'),
    f('gfrSampleDate', 'clinical', 'date'),
    f('serumCreatinine', 'clinical', 'name'),
    f('cumulativeDoseToDate', 'clinical', 'number', { decimals: true }),
    f('applicationForm', 'clinical', 'name'),
    f('carrierSolution', 'clinical', 'name'),
    f('carrierVolume', 'clinical', 'number'),
    f('finalVolume', 'clinical', 'number'),
    f('concentration', 'clinical', 'number', { decimals: true }),
    f('administrationDuration', 'clinical', 'number'),
    // sorted by the trigger-maintained medicine names: the prescribed name, else the active substance
    f('prescriptionTradeName', 'clinical', 'name', { sortKey: 'medicineNames' }),
    // Validation
    f('doctorValidatedBy', 'validation', 'name'),
    f('doctorValidatedOn', 'validation', 'date', { sortKey: 'doctorValidatedAt' }),
    f('pharmacistValidatedBy', 'validation', 'name'),
    f('pharmacistValidatedOn', 'validation', 'date', { sortKey: 'pharmacistValidatedAt' }),
    // Production
    f('preparationType', 'production', 'name'),
    f('pickingListNumber', 'production', 'name'),
    f('productionDay', 'production', 'date'),
    f('productionWorkstation', 'production', 'name'),
    f('tradeNameUsed', 'production', 'name', { default: true, cellGroup: 'tradeName' }),
    f('batchNumber', 'production', 'name', { default: true, cellGroup: 'tradeName' }),
    f('batchExpiry', 'production', 'date'),
    f('carrierBatchNumber', 'production', 'name'),
    f('carrierBatchExpiry', 'production', 'date'),
    f('quantityUsed', 'production', 'number', { default: true, cellGroup: 'quantity', decimals: true }),
    f('quantityUnit', 'production', 'name', { default: true, cellGroup: 'quantity' }),
    f('source', 'production', 'name'),
    f('restVialNumber', 'production', 'name'),
    f('restVialCreated', 'production', 'name'),
    f('producedBy', 'production', 'name'),
    f('producedOn', 'production', 'date'),
    f('useBy', 'production', 'date'),
    // Release and dispatch
    f('qaResult', 'release', 'name'),
    f('qaRejectionReason', 'release', 'longText'),
    f('releasedBy', 'release', 'name'),
    f('releasedOn', 'release', 'date'),
    f('dispatchedOn', 'release', 'date'),
    // Administration
    f('administeredBy', 'administration', 'name'),
    f('administeredOn', 'administration', 'date', { sortKey: 'administeredAt' }),
    f('administrationMode', 'administration', 'name'),
    f('scanCheck', 'administration', 'name'),
    f('notAdministeredReason', 'administration', 'longText'),
    f('returnedOn', 'administration', 'date'),
    // Cost and compliance
    f('unitPrice', 'cost', 'number', { decimals: true }),
    f('costOfProductUsed', 'cost', 'number', { decimals: true }),
    f('priorApproval', 'cost', 'name'),
    f('hospitalSystemOrderNumber', 'cost', 'name'),
    f('hospitalSystemMessage', 'cost', 'longText'),
];
/**
 * Default shown fields, in display order. The first block (Prescription number, Patient name +
 * Hospital ID) is what the table pins on the left, so it leads here too.
 */
export const DEFAULT_PRESCRIPTION_FIELD_IDS = [
    'prescriptionNumber',
    'patientName',
    'hospitalId',
    'applicationDate',
    'status',
    'protocol',
    'cycle',
    'day',
    'ward',
    'responsibleDoctor',
    'activeSubstance',
    'prescribedDose',
    'doseUnit',
    'tradeNameUsed',
    'batchNumber',
    'quantityUsed',
    'quantityUnit',
];
/**
 * Columns pinned to the left of the table while it scrolls sideways. The selection checkbox is
 * always first; these two follow it, as the design keeps the prescription number and the patient in view.
 */
export const FROZEN_FIELD_IDS = ['prescriptionNumber', 'patientName'];
const FIELD_BY_ID = new Map(PRESCRIPTION_REPORT_FIELDS.map(field => [field.id, field]));
export function getReportField(id) {
    return FIELD_BY_ID.get(id);
}
export const PRESCRIPTION_FIELD_SET = {
    fields: PRESCRIPTION_REPORT_FIELDS,
    frozenFieldIds: FROZEN_FIELD_IDS,
    defaultFieldIds: DEFAULT_PRESCRIPTION_FIELD_IDS,
    groupOrder: REPORT_GROUP_ORDER,
};
//# sourceMappingURL=prescription-fields.js.map